import React from 'react';
import { createPortal } from 'react-dom';
import { Row, Result, Button } from 'antd';
import SectionViewer from './SectionViewer';
import BackgroundVideo from '../backgroundVideo/BackgroundVideo';
import httpHandler from '../../../helpers/httpHandler';
import ModalViewer from '../elements/ModalViewer';
import Permissions from '../../../helpers/permissions';
import './pageViewer.css';
import { withRouter } from '../../reusableComponents/withRouter/WithRouter';
import { isGated } from '../elements/rulePermission';
import { buildScopedCss, pageScopeId } from './pageCustomCss';
import { ANCHOR_COVER_MAX_MS, hashAnchorId, revealAnchor } from './pageAnchors';
import { aliasRedirectTarget, isExternalTarget } from './pageAliases';
import FullScreenLoading from '../../reusableComponents/standard/fullScreenLoading/FullScreenLoading';

//A hard ceiling on the ruleset wait. `httpHandler.handleError` does NOT settle its
//promise on 401/426 (it redirects and returns without rejecting) nor on an error whose
//response carries no body, so awaiting it can hang FOREVER. That would leave every
//gated tile shimmering permanently — strictly worse than the bug this file fixes.
//Racing a timer guarantees the verdict window always closes.
const RULESET_TIMEOUT_MS = 15000;
//Small pause before the retry. The verdict is an uncached fan-out to Keap, which
//429s under load; two calls milliseconds apart amplify an outage instead of riding it out.
const RULESET_RETRY_DELAY_MS = 400;

const withTimeout = (promise, ms, label) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(label + ' timed out after ' + ms + 'ms')),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

//The page opened from the url (not a tab, popup or header, which come with a page_id).
//Only it follows a #anchor, so a deep link is handled exactly once (pageAnchors.js).
const isTopLevelPage = (props) =>
  props.page_id === undefined || props.page_id === null;

//The cover's own backstop, a little after revealAnchor's deadline: it lifts the cover even
//if the page request never settles (httpHandler can hang, see RULESET_TIMEOUT_MS above).
const COVER_BACKSTOP_MS = 1000;

class PageViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      //A deep link (#anchor): the "S" loading cover stays up until the page is at the anchor
      anchorCover: isTopLevelPage(props) && !!hashAnchorId(),
      visible: false,
      pageType: '',
      pageId: null,
      page: {
        pageSections: [],
        styles: {},
        details: {
          backgroundVideo: false,
          header: -1
        }
      },
      pageNotFound: false,
      ruleAccess: {},
      //false = the ruleset verdict is still outstanding. Gated elements must stay
      //inert until this flips, because ruleAccess[id] is `undefined` (falsy, and
      //therefore indistinguishable from "denied") for the whole of that window.
      ruleAccessReady: false
    };
  }
  //The path this instance last loaded for. Compared against window.location rather
  //than against props: App.tsx mounts this as `<PageViewer location={window.location} />`,
  //and window.location is ONE mutable object, so prevProps.location and this.props.location
  //are the same reference — comparing their pathnames was always false and the reset below
  //could never fire. Tracking the loaded path works for that mount AND the router one.
  loadedPath = null;
  //Bumped on every evaluation. A response for a page we have already navigated away
  //from must not be allowed to write its verdict over the current page's.
  rulesetRequestId = 0;

  componentDidMount = async () => {
    this.loadedPath = window.location.pathname;
    if (this.isTopLevel())
      window.addEventListener('hashchange', this.revealHashAnchor);
    //the target page then loads through componentDidUpdate (its path differs from loadedPath)
    if (this.redirectAlias()) return;
    if (this.state.anchorCover) this.startAnchorCover();
    await this.loadPage();
    this.revealHashAnchor();
  };

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.revealHashAnchor);
    if (this.cancelReveal) this.cancelReveal();
    clearTimeout(this.coverBackstop);
  }

  isTopLevel = () => isTopLevelPage(this.props);

  //An event alias such as /bsm reached by a click inside the app (a full page load was
  //already rewritten by pageAliasRedirect.js): go to the current event page instead, keeping
  //the ?query and #anchor. The new path then loads through componentDidUpdate as usual.
  redirectAlias = () => {
    if (!this.isTopLevel()) return false;
    const target = aliasRedirectTarget(window.location);
    if (!target) return false;
    //another site (the agenda is hidden: /bsm -> clubwealth.com/bsm): leave the app
    if (isExternalTarget(target)) window.location.replace(target);
    else this.props.navigate(target, { replace: true });
    return true;
  };

  //The cover's deadline counts from when the page starts loading, not from when it arrives.
  startAnchorCover = () => {
    this.coverDeadline = Date.now() + ANCHOR_COVER_MAX_MS;
    clearTimeout(this.coverBackstop);
    this.coverBackstop = setTimeout(
      this.liftAnchorCover,
      ANCHOR_COVER_MAX_MS + COVER_BACKSTOP_MS
    );
  };

  liftAnchorCover = () => {
    clearTimeout(this.coverBackstop);
    if (this.state.anchorCover) this.setState({ anchorCover: false });
  };

  revealHashAnchor = () => {
    if (!this.isTopLevel()) return;
    if (this.cancelReveal) this.cancelReveal();
    //a #hash changed on a page already open has no cover: it jumps as soon as it can
    this.cancelReveal = revealAnchor(hashAnchorId(), {
      coverDeadline: this.state.anchorCover ? this.coverDeadline : undefined,
      onSettled: this.liftAnchorCover
    });
  };

  componentDidUpdate = async () => {
    //page_id-driven mounts (popups, tabs, the default header) are not path-driven and
    //must not reload on navigation.
    if (this.props.page_id !== undefined && this.props.page_id !== null) return;
    const currentPath = window.location.pathname;
    if (currentPath === this.loadedPath) return;
    if (this.redirectAlias()) return;
    //Claim the path synchronously BEFORE the setState below, or that state change
    //re-enters componentDidUpdate with loadedPath still stale and we spin forever.
    this.loadedPath = currentPath;
    //The previous page's verdict does not apply here. Reset BEFORE reloading, or the
    //new page's gated tiles go live against a STALE ruleAccess map — worse than an
    //empty one, because it can answer confidently and wrongly.
    const anchorCover = !!hashAnchorId();
    this.setState({ ruleAccess: {}, ruleAccessReady: false, anchorCover });
    if (anchorCover) this.startAnchorCover();
    await this.loadPage();
    this.revealHashAnchor();
  };

  //Every element of the page, flattened.
  pageElements = (page) => {
    const elements = [];
    for (let pageSection of page.page.pageSections)
      for (let sectionRow of pageSection.sectionRows)
        for (let rowColumn of sectionRow.rowColumns)
          elements.push(...rowColumn.elements);
    return elements;
  };

  //The rule sets written into the page itself (tiles, buttons, images, carousel
  //slides, menu items). Known the moment the page arrives, with no further fetch, so
  //their check can start straight away. isGated is the same predicate the elements
  //use: collector and elements MUST agree on what counts as gated, or a tile waits on
  //a verdict that was never requested.
  collectPageRulesetIds = (page) => {
    let rulesetsToEvaluate = new Set();
    for (let element of this.pageElements(page)) {
      if (isGated(element.details))
        rulesetsToEvaluate.add(element.details.rule_set);
      if (element.type === 'Carousel') {
        for (let item of element.details.items) {
          if (isGated(item)) rulesetsToEvaluate.add(item.rule_set);
        }
      }
      if (element.type === 'NavBar') {
        element.details.navItems.forEach((item) => {
          //`!== -1` alone let null/absent rule_sets through, and the dropdown
          //branch had no filter at all — so `null` and `-1` were sent to the
          //permissions endpoint and every page carrying a NavBar made a live
          //Keap round-trip even when nothing on it was gated.
          if (isGated(item)) rulesetsToEvaluate.add(item.rule_set);
          if (item.dropDownItems.length) {
            item.dropDownItems.forEach((i) => {
              if (isGated(i)) rulesetsToEvaluate.add(i.rule_set);
            });
          }
        });
      }
    }
    return rulesetsToEvaluate;
  };

  //One Conferences block's sessions, as fetched to learn their rule sets.
  fetchConferenceOccurrences = (element) => {
    if (
      element.details.showSpecificConferences &&
      element.details.specificConferencesList.length > 0
    ) {
      //get conference occurrences by category and ids
      return httpHandler.get(
        '/api/conference/conferenceOccurrencesByCategoryAndIds',
        {
          category: element.details.category,
          ids: element.details.specificConferencesList.join(),
          time_frame: element.details.timeFrame,
          from_date: element.details.fromDate,
          to_date: element.details.toDate
        }
      );
    }
    //get conference occurrences occurrences by category
    return httpHandler.get('/api/conference/conferenceOccurrencesByCategory', {
      category: element.details.category,
      time_frame: element.details.timeFrame,
      from_date: element.details.fromDate,
      to_date: element.details.toDate
    });
  };

  //The rule sets of the sessions inside Conferences blocks. These need a fetch per
  //block, so they are collected separately and AFTER the page's own rule sets have
  //already been sent off. The blocks are fetched side by side: they used to be
  //awaited one after another, which on the dashboard (3 blocks) put three round
  //trips in front of every tile's verdict. A block whose fetch fails is skipped
  //(its own element reports its error); it must not hold up the others.
  collectConferenceRulesetIds = async (page) => {
    const blocks = this.pageElements(page).filter(
      (element) => element.type === 'Conferences'
    );
    const results = await Promise.all(
      blocks.map((element) =>
        withTimeout(
          this.fetchConferenceOccurrences(element),
          RULESET_TIMEOUT_MS,
          'conference occurrences'
        ).catch((error) => {
          console.error('could not read a Conferences block rule sets', error);
          return [];
        })
      )
    );
    let rulesetsToEvaluate = new Set();
    for (let occurrences of results) {
      for (let occurrence of occurrences || []) {
        const ruleSet =
          occurrence.status === 'recording_available'
            ? occurrence.recording_rule_set
            : occurrence.rule_set;
        if (ruleSet && ruleSet !== -1) rulesetsToEvaluate.add(ruleSet);
      }
    }
    return rulesetsToEvaluate;
  };

  //Asks the server for the verdict, once, with a timeout, then once more after a
  //short pause. Both attempts are bounded: an unbounded await here is what left
  //tiles shimmering forever.
  fetchRuleAccess = async (ruleSetIds) => {
    try {
      return await withTimeout(
        Permissions.getPermissionsForRuleSetIds(ruleSetIds),
        RULESET_TIMEOUT_MS,
        'ruleset evaluation'
      );
    } catch (error) {
      //Transient failure is a normal operating condition here, not an edge case: the
      //verdict needs a live call out to Keap (no cache, no server-side retry), and this
      //route requires a JWT that /api/pageBuilder/page does not — so it can 401 while
      //the page has already painted.
      console.error('ruleset evaluation failed, retrying once', error);
      await delay(RULESET_RETRY_DELAY_MS);
      return await withTimeout(
        Permissions.getPermissionsForRuleSetIds(ruleSetIds),
        RULESET_TIMEOUT_MS,
        'ruleset evaluation retry'
      );
    }
  };

  evaluateElementRulesets = async (page) => {
    const requestId = ++this.rulesetRequestId;
    //Every setState below is gated on this. Navigating away mid-flight bumps the id, so
    //a slow response for the PREVIOUS page can no longer clobber the current page's map
    //— which would leave a gated tile rendered live but permanently unclickable.
    const isCurrent = () => requestId === this.rulesetRequestId;

    //Adds a batch of answers to the map. The answers arrive in two batches (below),
    //so this merges rather than replaces.
    const addAnswers = (ruleSetAccessArray) => {
      if (!isCurrent()) return;
      //Defensive: a non-array payload used to throw outside every catch, taking the
      //whole evaluator down and leaving ruleAccessReady false forever.
      if (!Array.isArray(ruleSetAccessArray)) {
        console.error('unexpected ruleset payload', ruleSetAccessArray);
        return;
      }
      const answers = {};
      for (let accessItem of ruleSetAccessArray) {
        answers[accessItem.rule_set_id] = accessItem.permission;
      }
      this.setState((state) => ({
        ruleAccess: { ...state.ruleAccess, ...answers }
      }));
    };

    //Whatever fails — a conference fetch, the permissions call, a malformed payload —
    //the wait MUST end, or gated tiles shimmer forever. A rule set with no answer
    //stays INERT rather than falling back to "denied": permissionFor() returns
    //undefined and ElementViewer refuses to act on it. Failing to inert beats
    //redirecting a member who already paid to a sales page.
    const logFailure = (error) =>
      console.error(
        'ruleset evaluation failed; gated tiles will stay inert',
        error
      );

    try {
      //Batch 1: the page's own rule sets (the dashboard tiles). Sent at once, without
      //waiting for anything else, so the tiles get their answer in one round trip.
      const pageRulesets = this.collectPageRulesetIds(page);
      const pageBatch = pageRulesets.size
        ? this.fetchRuleAccess(Array.from(pageRulesets))
            .then(addAnswers)
            .catch(logFailure)
        : Promise.resolve();

      //Batch 2: the sessions inside Conferences blocks, only those not already asked.
      const conferenceBatch = this.collectConferenceRulesetIds(page).then(
        (conferenceRulesets) => {
          if (!isCurrent()) return;
          const remaining = Array.from(conferenceRulesets).filter(
            (id) => !pageRulesets.has(id)
          );
          if (!remaining.length) return;
          return this.fetchRuleAccess(remaining)
            .then(addAnswers)
            .catch(logFailure);
        }
      );

      await Promise.all([pageBatch, conferenceBatch]);
    } catch (error) {
      logFailure(error);
    }
    //Every question has had its answer (or failed): nothing on the page waits now.
    if (isCurrent()) this.setState({ ruleAccessReady: true });
  };

  loadPage = async () => {
    return await httpHandler
      .get('/api/pageBuilder/page', {
        url: window.location.pathname,
        id: this.props.page_id,
        defaultHeader: this.props.page_id === -2 ? 1 : null
      })
      .then((page) => {
        if (page) {
          this.setState(
            {
              page: page.page,
              pageType: page.type,
              pageId: page.id,
              pageNotFound: false
            },
            () => {}
          );
          //Belt and braces: evaluateElementRulesets catches its own failures, but a
          //throw in its synchronous prologue would otherwise become an unhandled
          //rejection and leave ruleAccessReady false forever.
          this.evaluateElementRulesets(page).catch((error) => {
            console.error('ruleset evaluation rejected', error);
            this.setState({ ruleAccessReady: true });
          });
        } else {
          this.setState({ pageNotFound: true, anchorCover: false });
        }
      })
      .catch((error) => {
        console.error('error', error);
        this.liftAnchorCover();
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handlePopup = (popupId) => {
    httpHandler
      .get('/api/pageBuilder/pages', { type: 'popup' })
      .then((popups) => {
        let popup = popups.filter((pop) => pop.id === popupId);
        if (popup.length > 0) {
          this.setState({ selectedPopup: popup[0] }, this.showModal);
        }
      })
      .catch((error) => {})
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  showModal = () => {
    this.setState((prevState) => ({
      visible: true
    }));
  };

  closeModal = () => {
    this.setState((prevState) => ({
      visible: false
    }));
  };

  render() {
    let topLevelStyles = {};
    if (this.state.pageType === 'standard') topLevelStyles.minHeight = '100vh';
    if (this.state.pageNotFound) {
      return (
        <div>
          <Result
            status='404'
            title='404'
            subTitle='Sorry, the page you visited does not exist.'
            extra={
              <Button onClick={() => window.history.back()} type='primary'>
                Go Back
              </Button>
            }
          />
        </div>
      );
    }
    //Optional "Custom CSS (this page only)": scoped under this page's id, so it cannot style
    //any other page. No custom CSS = no <style> tag, exactly as before (pageCustomCss.js).
    const scopedCss = buildScopedCss(
      this.state.pageId,
      this.state.page?.details?.customCss
    );
    return (
      <div id={pageScopeId(this.state.pageId)} style={topLevelStyles}>
        {scopedCss ? <style>{scopedCss}</style> : null}
        {this.state.anchorCover
          ? createPortal(
              <div
                data-pb-anchor-cover='true'
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 10000,
                  overflow: 'hidden'
                }}
              >
                <FullScreenLoading loading={true} />
              </div>,
              document.body
            )
          : null}
        {/* <HelpPanel close={this.props.close} expand={this.props.expand}/> */}
        <Row type='flex' justify='center'>
          <div style={this.state.page?.styles}>
            {this.state.page.details.backgroundVideo ? (
              <BackgroundVideo
                sourceType={this.state.page.details.sourceType}
                source={this.state.page.details.source}
              />
            ) : (
              ''
            )}
            {this.state.page.pageSections.map((pageSection, index) => {
              return (
                <SectionViewer
                  key={index}
                  pageHistory={this.props.history}
                  pageSection={pageSection}
                  handlePopup={this.handlePopup}
                  ruleAccess={this.state.ruleAccess}
                  ruleAccessReady={this.state.ruleAccessReady}
                />
              );
            })}
          </div>
        </Row>
        <ModalViewer
          visible={this.state.visible}
          closeModal={this.closeModal}
          pageId={this.state.selectedPopup ? this.state.selectedPopup.id : null}
          curPopup={this.state.selectedPopup}
        />
      </div>
    );
  }
}

export default withRouter(PageViewer);
